import axiosInstance from 'api/axios';
import { APIResponse } from '../types';

export interface TwitterPostResponse {
  query: string;
}

export interface ScheduleTweetRequest {
  tweet: string;
  scheduled_at: string;
  id: number;
}

export interface UpdateTweetRequest {
  tweet: string;
  scheduled_at: string;
}

export interface TwitterAuthResponse {
  url: string;
  state: string;
}

export interface TwitterAuthCompleteResponse {
  twitter_user_id: string;
}

interface PostTweetPayload {
  target_handle: string;
  id?: string;
}

interface ScheduleTweetPayload {
  target_handle: string;
  scheduled_at: string;
  id?: string;
}

export const twitterService = {
  // Twitter Authentication
  getAuthUrl: async (): Promise<APIResponse<TwitterAuthResponse>> => {
    const response = await axiosInstance.private.get(
      '/api/v1/social-media/twitter/auth'
    );
    return response.data;
  },

  // Twitter Callback
  handleCallback: async (
    state: string, // required
    code: string, // required
    error?: string, // optional
    error_description?: string // optional
  ): Promise<APIResponse<TwitterAuthResponse>> => {
    const response = await axiosInstance.private.get(
      `/api/v1/social-media/twitter/callback`,
      {
        params: {
          state,
          code,
          error,
          error_description,
        },
      }
    );
    return response.data;
  },

  // Complete Twitter Authentication
  completeAuth: async ({
    twitter_access_token,
    twitter_refresh_token,
  }: {
    twitter_access_token: string;
    twitter_refresh_token: string;
  }): Promise<APIResponse<TwitterAuthCompleteResponse>> => {
    const response = await axiosInstance.private.put(
      '/api/v1/social-media/twitter/auth/complete',
      {
        twitter_access_token,
        twitter_refresh_token,
      }
    );
    return response.data;
  },

  // Post Tweet
  postTweet: async (payload: PostTweetPayload): Promise<APIResponse<TwitterPostResponse>> => {
    const response = await axiosInstance.private.post(
      '/api/v1/social-media/twitter/publish',
      payload
    );
    return response.data;
  },

  // Schedule Tweet
  scheduleTweet: async (payload: ScheduleTweetPayload): Promise<APIResponse<TwitterPostResponse>> => {
    const response = await axiosInstance.private.post(
      '/api/v1/social-media/twitter/schedule',
      payload
    );
    return response.data;
  },

  // Get All Tweets
  getAllTweets: async (
    access_token: string
  ): Promise<APIResponse<TwitterPostResponse[]>> => {
    const response = await axiosInstance.private.get(
      '/api/v1/social-media/twitter/tweets',
      {
        headers: {
          Cookie: `access_token=${access_token}`,
        },
      }
    );
    return response.data;
  },

  // Delete Tweet
  deleteTweet: async (
    id: number,
    access_token: string
  ): Promise<APIResponse<number>> => {
    const response = await axiosInstance.private.delete(
      `/api/v1/social-media/twitter/tweets/${id}`,
      {
        headers: {
          Cookie: `access_token=${access_token}`,
        },
      }
    );
    return response.data;
  },

  // Update Tweet
  updateTweet: async (
    tweetId: number,
    data: UpdateTweetRequest
  ): Promise<APIResponse<TwitterPostResponse>> => {
    const response = await axiosInstance.private.put(
      `/api/v1/social-media/twitter/tweets/${tweetId}`,
      { data },
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },
};

export default twitterService;
